import { BrokerageProducerSearchRequestQuery, BrokerageProducerSearchResponse } from '@lio/api-types';

import { useInfiniteQuery } from '@tanstack/react-query';
import { useClient } from '@utils/api-client';
import { brokerageProducerKeys } from './user.keys';

export const getBrokerageProducerQuery = ({
  pageLimit,
  pageOffset,
  sort,
  searchText,
  brokerageId,
  hasPoliciesInBrokerageId,
}: BrokerageProducerSearchRequestQuery) => {
  const query = new URLSearchParams();
  if (pageLimit !== undefined) {
    query.append('pageLimit', String(pageLimit));
  }
  if (pageOffset !== undefined) {
    query.append('pageOffset', String(pageOffset));
  }
  if (sort) {
    query.append('sort', sort.toString());
  }
  if (searchText) {
    query.append('searchText', searchText.toString());
  }
  if (brokerageId) {
    query.append('brokerageId', brokerageId.toString());
  }
  if (hasPoliciesInBrokerageId) {
    query.append('hasPoliciesInBrokerageId', hasPoliciesInBrokerageId.toString());
  }
  return query.toString();
};

export const useSearchPaginatedBrokerageProducers = (
  requestQuery?: BrokerageProducerSearchRequestQuery,
  { enabled } = { enabled: true },
) => {
  const client = useClient();
  const query = getBrokerageProducerQuery(requestQuery ?? {});
  const result = useInfiniteQuery({
    queryKey: brokerageProducerKeys.searchPaginated(requestQuery ?? {}, query),

    queryFn: ({ pageParam }) =>
      client.get(`brokerage-producer?${query}&pageOffset=${pageParam}`).json<BrokerageProducerSearchResponse>(),
    enabled,
    initialPageParam: 0,
    staleTime: 0,
    gcTime: 0,
    placeholderData: (previousData) => previousData,
    refetchOnWindowFocus: false,
    retry: false,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage?.pagination) return undefined;
      if (!lastPage.pagination.pageLimit) return undefined;
      if (!lastPage.pagination.total) return undefined;
      const { total, pageLimit } = lastPage.pagination;
      const fetchedPageCount = allPages.length;
      const nextPageOffsetValue = fetchedPageCount * pageLimit;
      if (nextPageOffsetValue > total) return undefined;
      return nextPageOffsetValue;
    },
  });
  return { ...result, brokerageProducers: result.data };
};
